import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "../api";
import type { Binding, CollectionDetail, CreateItem, Status, ViewType } from "../api/types";
import { Icon, type IconName } from "../components/icons";
import { ConnectorBadge, ErrorBox, Spinner } from "../components/bits";
import { AddBindingDialog } from "../components/AddBindingDialog";
import { EnableBucketsDialog, toggleBuckets } from "../components/BucketDialogs";
import { MenuButton, useContextMenu } from "../components/ContextMenu";
import { formatDateTime } from "../lib/format";
import {
  createItem,
  loadCollection,
  loadCollections,
  loadItems,
  setCollectionArchived,
  syncReportText,
  toast,
  toastError,
} from "../store/app";
import { useApp, useBuckets, useCollectionItems } from "../store/hooks";
import { BoardView } from "./BoardView";
import { BucketBoardView } from "./BucketBoardView";
import { CalendarView } from "./CalendarView";
import { TimelineView } from "./TimelineView";
import { ListView } from "./ListView";

/** Server view types plus the client-side "buckets" board (v0.4, when buckets are enabled). */
type PageView = ViewType | "buckets";
const VIEW_TYPES: ViewType[] = ["board", "list", "calendar", "timeline"];
export const VIEW_NAMES: Record<PageView, string> = {
  board: "Board",
  buckets: "Buckets",
  list: "List",
  calendar: "Calendar",
  timeline: "Timeline",
};
export const VIEW_ICONS: Record<PageView, IconName> = {
  board: "board",
  buckets: "bucket",
  list: "list",
  calendar: "calendar",
  timeline: "timeline",
};
interface ViewTab {
  id: string;
  name: string;
  type: PageView;
}

export function sortedStatuses(detail: CollectionDetail | undefined): Status[] {
  return detail ? [...detail.statuses].sort((a, b) => a.position - b.position) : [];
}

export function firstStatusOf(statuses: Status[], cats: Status["category"][]): Status | undefined {
  for (const c of cats) {
    const s = statuses.find((x) => x.category === c);
    if (s) return s;
  }
  return statuses[0];
}

function SyncButton({ binding }: { binding: Binding }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="binding-chip" title={binding.last_error ?? undefined}>
      <ConnectorBadge type={binding.connector_type} />
      <span className="binding-name">{binding.remote_collection_name}</span>
      <span className="muted small">{binding.mode.replace("_", " ")}</span>
      {binding.last_error ? (
        <span className="sync-badge error" title={binding.last_error}>
          <span className="dot" aria-hidden="true" />
          error
        </span>
      ) : (
        binding.last_synced_at && (
          <span className="muted small" title={binding.last_synced_at}>
            synced {formatDateTime(binding.last_synced_at)}
          </span>
        )
      )}
      <button
        type="button"
        className="btn small"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const report = await api.syncBinding(binding.id);
            toast(syncReportText(report), report.errors.length ? "error" : "success", 7000);
            await Promise.all([loadItems(binding.collection_id), loadCollection(binding.collection_id)]);
          } catch (e) {
            toastError(e, "Sync failed");
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Syncing…" : "Sync now"}
      </button>
    </div>
  );
}

export function CollectionPage() {
  const { collectionId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const detail = useApp((s) => s.details[collectionId]);
  const loaded = useApp((s) => !!s.loadedCollections[collectionId]);
  const version = useApp((s) => s.collectionVersion[collectionId] ?? 0);
  const items = useCollectionItems(collectionId);
  const [error, setError] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState("");
  const [unarchiving, setUnarchiving] = useState(false);
  const [dialog, setDialog] = useState<"enable-buckets" | "link" | null>(null);
  const bucketsEnabled = !!detail?.collection.buckets_enabled;
  const buckets = useBuckets(collectionId, bucketsEnabled);

  const reload = () => {
    setError(null);
    Promise.all([loadCollection(collectionId), loadItems(collectionId)]).catch((e) => setError(errorMessage(e)));
  };

  useEffect(() => {
    if (!collectionId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, version]);

  const statuses = useMemo(() => sortedStatuses(detail), [detail]);
  // Always offer the four view types; use the server's view of a type when present.
  const views: ViewTab[] = useMemo(() => {
    const tabs: ViewTab[] = VIEW_TYPES.map((type) => {
      const v = detail?.views.find((x) => x.type === type);
      return v ? { id: v.id, name: v.name, type } : { id: type, name: VIEW_NAMES[type], type };
    });
    if (detail?.collection.buckets_enabled) tabs.splice(1, 0, { id: "buckets", name: VIEW_NAMES.buckets, type: "buckets" });
    return tabs;
  }, [detail]);
  const viewParam = params.get("view");
  const view =
    views.find((v) => v.id === viewParam) ??
    views.find((v) => v.type === viewParam) ??
    (bucketsEnabled
      ? views.find((v) => v.type === "buckets")
      : detail?.collection.kind === "board" || detail?.collection.kind === "project"
        ? views.find((v) => v.type === "board")
        : views.find((v) => v.type === "list"))!;
  const viewType = view.type;
  const labelsSeen = useMemo(() => [...new Set(items.flatMap((i) => i.labels))].sort(), [items]);
  const bucketsTarget = detail
    ? { id: collectionId, name: detail.collection.name, buckets_enabled: bucketsEnabled, bindings: detail.bindings }
    : null;
  const pageMenu = useContextMenu(
    () => [
      {
        label: bucketsEnabled ? "Disable buckets" : "Enable buckets",
        icon: "bucket",
        disabled: !!detail?.collection.archived_at,
        onSelect: () =>
          bucketsTarget &&
          toggleBuckets(
            bucketsTarget,
            () => setDialog("enable-buckets"),
            (on) => setParams(on ? { view: "buckets" } : {}, { replace: true }),
          ),
      },
      {
        label: "Link to external list…",
        icon: "link",
        disabled: !!detail?.collection.archived_at,
        onSelect: () => setDialog("link"),
      },
    ],
    "Collection actions",
  );

  if (error && !detail) return <div className="page"><ErrorBox error={error} onRetry={reload} /></div>;
  if (!detail) return <Spinner />;

  const add = async (body: CreateItem) => {
    try {
      await createItem(collectionId, body);
    } catch (e) {
      toastError(e, "Could not create item");
      throw e;
    }
  };

  const archived = !!detail.collection.archived_at;

  return (
    <div className={`page collection-page${archived ? " is-archived" : ""}`}>
      {archived && (
        <div className="archived-banner" role="status">
          <span>
            <strong>Archived</strong> — sync paused. Items stay readable.
          </span>
          <button
            type="button"
            className="btn small"
            disabled={unarchiving}
            onClick={async () => {
              setUnarchiving(true);
              try {
                await setCollectionArchived(collectionId, false, true);
              } catch (e) {
                toastError(e, "Unarchive failed");
              } finally {
                setUnarchiving(false);
              }
            }}
          >
            {unarchiving ? "Unarchiving…" : "Unarchive"}
          </button>
        </div>
      )}
      <header className="page-head">
        {editingName ? (
          <form
            className="title-edit"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.patchCollection(collectionId, { name: name.trim() });
                await Promise.all([loadCollection(collectionId), loadCollections(detail.collection.space_id)]);
                setEditingName(false);
              } catch (err) {
                toastError(err, "Rename failed");
              }
            }}
          >
            <input
              aria-label="Collection name"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setEditingName(false)}
              onBlur={() => setEditingName(false)}
            />
          </form>
        ) : (
          <h1>
            <button
              type="button"
              className="title-btn"
              title="Rename"
              disabled={archived}
              onClick={() => {
                setName(detail.collection.name);
                setEditingName(true);
              }}
            >
              {detail.collection.name}
            </button>
          </h1>
        )}
        <div className="seg" role="tablist" aria-label="View">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={v.id === view.id}
              className={v.id === view.id ? "active" : ""}
              onClick={() => setParams({ view: v.id }, { replace: true })}
            >
              <Icon name={VIEW_ICONS[v.type]} size={14} />
              <span className="seg-label">{v.name}</span>
            </button>
          ))}
        </div>
        <span className="page-menu" {...pageMenu.targetProps}>
          <MenuButton label="Collection actions" expanded={pageMenu.isOpen} onOpen={pageMenu.openFrom} />
          {pageMenu.menu}
        </span>
      </header>

      {detail.bindings.length > 0 && (
        <div className="bindings-row">
          {detail.bindings.map((b) => (
            <SyncButton key={b.id} binding={b} />
          ))}
          {!archived && (
            <button type="button" className="btn small ghost" onClick={() => setDialog("link")}>
              + Link list
            </button>
          )}
        </div>
      )}
      {dialog === "enable-buckets" && bucketsTarget && (
        <EnableBucketsDialog
          collection={bucketsTarget}
          onClose={() => setDialog(null)}
          onEnabled={() => setParams({ view: "buckets" }, { replace: true })}
        />
      )}
      {dialog === "link" && (
        <AddBindingDialog
          collectionId={collectionId}
          spaceId={detail.collection.space_id}
          bucketsEnabled={bucketsEnabled}
          buckets={buckets}
          onClose={() => setDialog(null)}
        />
      )}

      {!loaded ? (
        <Spinner />
      ) : viewType === "board" ? (
        <BoardView collectionId={collectionId} statuses={statuses} items={items} onCreate={add} />
      ) : viewType === "buckets" ? (
        <BucketBoardView
          collectionId={collectionId}
          statuses={statuses}
          items={items}
          buckets={buckets}
          bindings={detail.bindings}
          onCreate={add}
        />
      ) : viewType === "calendar" ? (
        <CalendarView items={items} />
      ) : viewType === "timeline" ? (
        <TimelineView items={items} statuses={statuses} />
      ) : (
        <ListView
          collectionId={collectionId}
          statuses={statuses}
          items={items}
          onCreate={add}
          labels={labelsSeen}
          buckets={bucketsEnabled ? buckets : undefined}
        />
      )}
    </div>
  );
}
